import React from 'react';
import PropTypes from 'prop-types';

import IChart from '@meronex/icons/fi/FiBarChart2';

import MdClose from '@meronex/icons/md/MdClose';

import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';

import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { analyticsMng } from '../../api/analyticsMng';
import Stats from './Stats';

const DATE_TYPE_OPTIONS = [
  { value: 'lastMonth', label: 'Last Month' },
  { value: 'lastThreeMonths', label: 'Last 3 Months' },
];

function StoreAnalyticsDialog(props) {
  const theme = useTheme();

  const { open, onClose, storeId } = props;
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const isDark = theme.palette.type === 'dark';

  const [filters, setFilters] = React.useState({
    dateType: 'lastThreeMonths',
    range: [null, null],
    out: true,
    transferOut: true,
    in: true,
    transferIn: true,
  });

  const [fetching, setFetching] = React.useState(false);
  const [fetchError, setFetchError] = React.useState(null);

  const [itemsMap, setItemsMap] = React.useState();

  React.useEffect(() => {
    if (open && storeId) {
      fetchItemTransaction();
    }
  }, [filters, storeId, open]);

  React.useEffect(() => {
    if (!open) {
      setItemsMap(undefined);
      setFetchError(null);
    }
  }, [open]);

  const processTransactions = (transactions) => {
    const _itemsMap = new Map();

    transactions.forEach((tx) => {
      let itemData = _itemsMap.get(tx.storeItem.type._id);

      if (!itemData) {
        itemData = {
          storeItem: tx.storeItem,
          in: 0,
          out: 0,
          transferIn: 0,
          transferOut: 0,
          firstTx: undefined,
          lastTx: undefined,
        };
      }
      const amount = parseFloat(tx.amount) || 0;
      if (tx.transactionType === 'in') {
        itemData.in = amount + itemData.in;
      } else if (tx.transactionType === 'out') {
        itemData.out = amount + itemData.out;
      } else if (tx.transactionType === 'transferOut') {
        itemData.transferOut = amount + itemData.transferOut;
      } else if (tx.transactionType === 'transferIn') {
        itemData.transferIn = amount + itemData.transferIn;
      }

      if (!itemData.firstTx) {
        itemData.firstTx = tx;
      } else {
        itemData.lastTx = tx;
      }
      _itemsMap.set(tx.storeItem.type._id, itemData);
    });
    setItemsMap(_itemsMap);
  };
  const fetchItemTransaction = async () => {
    setFetching(true);
    setFetchError(null);
    try {
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out. Try a shorter time range.')), 60000)
      );
      const transactions = await Promise.race([
        analyticsMng.getAnalyticsTransactions({ storeId, filters }),
        timeoutPromise,
      ]);
      if (!Array.isArray(transactions)) {
        setFetchError('No data returned');
        return;
      }
      processTransactions(transactions);
    } catch (e) {
      console.error('Analytics fetch error:', e);
      setFetchError(e?.reason || e?.message || 'Failed to load analytics. Try a shorter time range.');
    } finally {
      setFetching(false);
    }
  };

  const currentLabel = DATE_TYPE_OPTIONS.find(o => o.value === filters.dateType)?.label || 'Last 3 Months';

  return (
    <Dialog
      className={isDesktop ? '' : 'fullScreenMobile'}
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: isDesktop
          ? { maxHeight: '80vh', borderRadius: '16px', overflow: 'hidden' }
          : {},
      }}>
      {/* Custom header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 20px',
        borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#f1f5f9'}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '10px',
            backgroundColor: isDark ? 'rgba(99, 102, 241, 0.15)' : '#eef2ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <IChart size={18} style={{ color: '#6366f1' }} />
          </div>
          <div>
            <div style={{ fontSize: '15px', fontWeight: 600, color: isDark ? '#f1f5f9' : '#0f172a' }}>
              Store Analytics
            </div>
            <div style={{ fontSize: '12px', color: isDark ? 'rgba(255,255,255,0.4)' : '#94a3b8' }}>
              Transaction summary
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {/* Period selector */}
        <select
          value={filters.dateType}
          onChange={(e) => setFilters({ ...filters, dateType: e.target.value })}
          style={{
            fontSize: '12px',
            fontWeight: 500,
            padding: '6px 28px 6px 10px',
            borderRadius: '8px',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : '#e2e8f0'}`,
            backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc',
            color: isDark ? '#f1f5f9' : '#334155',
            cursor: 'pointer',
            outline: 'none',
            appearance: 'none',
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 8px center',
          }}
        >
          {DATE_TYPE_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <div
          onClick={onClose}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '36px',
            height: '36px',
            borderRadius: '8px',
            cursor: 'pointer',
            color: isDark ? '#94a3b8' : '#64748b',
            flexShrink: 0,
            fontSize: '20px',
            transition: 'background-color 0.15s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}>
          <MdClose />
        </div>
        </div>
      </div>
      <DialogContent
        style={{
          padding: '0',
        }}>
        <Stats itemsMap={itemsMap} fetching={fetching} fetchError={fetchError} />
      </DialogContent>
    </Dialog>
  );
}

StoreAnalyticsDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

export default StoreAnalyticsDialog;
